import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

type RefType = {
  current: HTMLElement | null;
};

/**
 * Animates the section title with a fade-in and slide-up effect
 */
export const animateTitle = (
  titleRef: RefType,
  sectionRef: RefType,
  delay: number = 0
): void => {
  if (!titleRef.current || !sectionRef.current) return;

  gsap.fromTo(
    titleRef.current,
    { opacity: 0, y: 20 },
    {
      opacity: 1,
      y: 0,
      duration: 0.7,
      delay,
      ease: "power3.out",
      scrollTrigger: {
        id: "aboutLocations-title",
        trigger: sectionRef.current,
        start: "top 80%",
        once: true,
      },
    }
  );
};

/**
 * Animates location cards with a staggered fade-in effect
 */
export const animateLocationCards = (
  locationRefs: RefType[],
  sectionRef: RefType,
  delay: number = 0.2
): void => {
  if (!sectionRef.current || !locationRefs.length) return;

  const validRefs = locationRefs
    .filter((ref) => ref.current !== null)
    .map((ref) => ref.current!);

  if (!validRefs.length) return;

  gsap.fromTo(
    validRefs,
    {
      opacity: 0,
      y: 30,
      scale: 0.95,
    },
    {
      opacity: 1,
      y: 0,
      scale: 1,
      duration: 0.7,
      stagger: 0.2,
      delay,
      ease: "power3.out",
      scrollTrigger: {
        id: "aboutLocations-cards",
        trigger: sectionRef.current,
        start: "top 80%",
        once: true,
      },
    }
  );
};

/**
 * Animates maps with a fade-in and slight scale effect
 */
export const animateMaps = (
  mapContainers: RefType[],
  sectionRef: RefType,
  delay: number = 0.3
): void => {
  if (!sectionRef.current || !mapContainers.length) return;

  const validMapContainers = mapContainers
    .filter((ref) => ref.current !== null)
    .map((ref) => ref.current!);

  if (!validMapContainers.length) return;

  gsap.fromTo(
    validMapContainers,
    {
      opacity: 0,
      scale: 0.9,
    },
    {
      opacity: 1,
      scale: 1,
      duration: 0.8,
      stagger: 0.3,
      delay,
      ease: "power2.out",
      scrollTrigger: {
        id: "aboutLocations-maps",
        trigger: sectionRef.current,
        start: "top 80%",
        once: true,
      },
    }
  );
};

/**
 * Cleans up all ScrollTrigger instances for this component
 */
export const cleanupScrollTriggers = (): void => {
  ScrollTrigger.getAll()
    .filter((trigger) => trigger.vars.id?.startsWith("aboutLocations"))
    .forEach((trigger) => trigger.kill());
};
